import QRCode from 'qrcode';
import { byId, isAr, message, saveBlob, copyText, trackEvent } from './extra-common.js';

// ============================================================================
// Curated Wordlists for Memorable Diceware Passphrases
// ============================================================================
const ARABIC_DICEWARE = [
  'صقر', 'نمر', 'بحر', 'شمس', 'قمر', 'نجم', 'جبل', 'وادي', 'سحاب', 'مطر',
  'نهر', 'نخيل', 'زيتون', 'عنبر', 'مسك', 'زمرد', 'ياقوت', 'ذهب', 'فضة', 'حديد',
  'سيف', 'درع', 'فارس', 'قلعة', 'حصن', 'فلك', 'مدار', 'كوكب', 'غيمة', 'واحة',
  'رمال', 'بستان', 'زهرة', 'عطر', 'نسيم', 'برق', 'رعد', 'شاطئ', 'جزيرة', 'مرجان',
  'حوت', 'نسر', 'خيل', 'رمح', 'تاج', 'راية', 'قمة', 'فجر', 'ضياء', 'شفق',
  'سهم', 'فضاء', 'شهاب', 'ريحان', 'سرو', 'أرز', 'غزال', 'طير', 'عقاب', 'شاهين',
  'بلسم', 'عقيق', 'درة', 'سوار', 'محراب', 'منارة', 'سفينة', 'شراع', 'بوصلة', 'مرسى',
  'موج', 'سديم', 'نبراس', 'شهب', 'نور', 'شعاع', 'صهوة', 'جواد', 'بيداء', 'هضبة',
  'كهف', 'ينبوع', 'غدير', 'وافر', 'ظفر', 'رياح', 'أثير', 'فردوس', 'روضة', 'سندس',
  'زبرجد', 'ياقوتة', 'مرمر', 'بلور', 'أصيل', 'صهيل', 'بارق', 'طلائع', 'نبراس', 'سراج',
  'برهان', 'يقين', 'سكون', 'هديل', 'يمام', 'طاووس', 'كركي', 'بلبل', 'كناري', 'صنوبر',
  'خزامى', 'ياسمين', 'نرجس', 'شقائق', 'عوسج', 'أثل', 'غضا', 'صفصاف', 'حور', 'رمان',
  'تين', 'عنب', 'نارنج', 'ليمون', 'كافور', 'صندل', 'لبان', 'عود', 'زعفران', 'قرنفل'
];

const ENGLISH_DICEWARE = [
  'falcon', 'tiger', 'ocean', 'solar', 'lunar', 'comet', 'planet', 'alpine', 'valley', 'cloud',
  'river', 'timber', 'amber', 'silver', 'golden', 'knight', 'castle', 'fortress', 'rocket', 'galaxy',
  'orbit', 'meadow', 'desert', 'oasis', 'blossom', 'breeze', 'thunder', 'lightning', 'island', 'coral',
  'eagle', 'stallion', 'crown', 'summit', 'aurora', 'nebula', 'canyon', 'shadow', 'crystal', 'quartz',
  'beacon', 'compass', 'anchor', 'ember', 'blizzard', 'phoenix', 'horizon', 'zenith', 'vortex', 'pulsar',
  'meteor', 'forest', 'shield', 'banner', 'ranger', 'voyage', 'sailor', 'harbor', 'glacier', 'stream',
  'mirage', 'citadel', 'copper', 'cobalt', 'granite', 'marble', 'velvet', 'echo', 'spirit', 'arrow',
  'avalanche', 'badger', 'bamboo', 'basalt', 'bison', 'cascade', 'cedar', 'chameleon', 'cliff', 'conifer',
  'coyote', 'crag', 'cypress', 'delta', 'dune', 'elm', 'fjord', 'flint', 'fox', 'geyser',
  'grove', 'hawk', 'heather', 'heron', 'jaguar', 'juniper', 'lagoon', 'larch', 'lynx', 'magma',
  'maple', 'monsoon', 'moss', 'obsidian', 'osprey', 'otter', 'panther', 'peak', 'pine', 'prairie',
  'rapids', 'ravine', 'reef', 'ridge', 'sable', 'sequoia', 'sierra', 'sparrow', 'spruce', 'tundra'
];

// ============================================================================
// Cryptographically Secure Random Utilities
// ============================================================================
function secureIndex(max) {
  if (max <= 1) return 0;
  const limit = Math.floor(256 / max) * max;
  const buf = new Uint8Array(64);
  while (true) {
    crypto.getRandomValues(buf);
    for (const value of buf) {
      if (value < limit) return value % max;
    }
  }
}

function randomChar(chars) {
  return chars[secureIndex(chars.length)];
}

function shuffle(array) {
  for (let i = array.length - 1; i > 0; i--) {
    const j = secureIndex(i + 1);
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
}

// ============================================================================
// Attack Matrix & Human-Readable Time Formatter
// ============================================================================
function formatTimeEstimate(seconds, isArabic) {
  if (!Number.isFinite(seconds) || seconds <= 0) {
    return isArabic ? 'فوري (< ثانية)' : 'Instant (< 1s)';
  }
  if (seconds < 1) return isArabic ? 'فوري (< ثانية)' : 'Instant (< 1s)';
  if (seconds < 60) return isArabic ? `${Math.round(seconds)} ثانية` : `${Math.round(seconds)}s`;
  if (seconds < 3600) return isArabic ? `${Math.round(seconds / 60)} دقيقة` : `${Math.round(seconds / 60)} min`;
  if (seconds < 86400) return isArabic ? `${Math.round(seconds / 3600)} ساعة` : `${Math.round(seconds / 3600)} hrs`;
  if (seconds < 86400 * 30) return isArabic ? `${Math.round(seconds / 86400)} يوم` : `${Math.round(seconds / 86400)} days`;
  if (seconds < 86400 * 365) return isArabic ? `${Math.round(seconds / (86400 * 30))} شهر` : `${Math.round(seconds / (86400 * 30))} mos`;

  const years = seconds / (86400 * 365.25);
  if (years < 1000) return isArabic ? `${Math.round(years)} سنة` : `${Math.round(years)} yrs`;
  if (years < 1e6) return isArabic ? `${(years / 1000).toFixed(1)} ألف سنة` : `${(years / 1000).toFixed(1)}k yrs`;
  if (years < 1e9) return isArabic ? `${(years / 1e6).toFixed(1)} مليون سنة` : `${(years / 1e6).toFixed(1)}M yrs`;
  if (years < 1e12) return isArabic ? `${(years / 1e9).toFixed(1)} مليار سنة` : `${(years / 1e9).toFixed(1)}B yrs`;
  return isArabic ? 'مستحيل رياضياً (> عمر الكون)' : 'Mathematically unbreakable (> Age of Universe)';
}

// ============================================================================
// State & Elements
// ============================================================================
let currentMode = 'chars';
let currentPassword = '';
let isMasked = false;
let qrVisible = true;

// Core Elements
const displayFormattedEl = byId('pg-display-formatted');
const outputInputEl = byId('pg-output');
const toggleVisBtn = byId('pg-toggle-visibility');
const generateBtn = byId('pg-generate');
const showQrBtn = byId('pg-show-qr');
const copyBtn = byId('pg-copy');
const copyTextSpan = byId('pg-copy-text');
const qrBox = byId('pg-qr-box');
const qrContainer = byId('pg-qr-container');

// Strength & Meta Elements
const strengthSegments = byId('pg-strength-segments');
const strengthMeter = byId('pg-strength');
const strengthLabel = byId('pg-strength-label');
const entropyLabel = byId('pg-entropy');
const crackTimeLabel = byId('pg-crack-time');

// Attack Matrix Elements
const crackThrottledEl = byId('crack-throttled');
const crackFastEl = byId('crack-fast');
const crackGpuEl = byId('crack-gpu');
const crackSuperEl = byId('crack-super');

// Composition Chips
const chipLen = byId('chip-len');
const chipUpper = byId('chip-upper');
const chipLower = byId('chip-lower');
const chipNum = byId('chip-num');
const chipSym = byId('chip-sym');

// Controls Elements
const lengthSlider = byId('pg-length');
const lengthNumInput = byId('pg-length-num');
const lengthOut = byId('pg-length-out');
const wordsSlider = byId('pg-words');
const wordsOut = byId('pg-words-out');
const passphraseLang = byId('pg-passphrase-lang');
const separatorSelect = byId('pg-separator');
const passphraseCase = byId('pg-passphrase-case');
const passphraseAddExtras = byId('pg-passphrase-add-extras');
const pinLengthInput = byId('pg-pin-length');
const bulkCountSelect = byId('pg-bulk-count');
const bulkListContainer = byId('pg-bulk-list');
const auditInput = byId('pg-audit-input');
const auditToggleEye = byId('pg-audit-toggle-eye');
const auditResultsContainer = byId('pg-audit-results');

// ============================================================================
// Core Generator Algorithms
// ============================================================================
function generateCustomPassword() {
  const length = Number(lengthSlider?.value || 20);

  const charsets = [];
  if (byId('pg-lower')?.checked) charsets.push(['lower', 'abcdefghijklmnopqrstuvwxyz']);
  if (byId('pg-upper')?.checked) charsets.push(['upper', 'ABCDEFGHIJKLMNOPQRSTUVWXYZ']);
  if (byId('pg-number')?.checked) charsets.push(['number', '0123456789']);
  if (byId('pg-symbol')?.checked) charsets.push(['symbol', '!@#$%^&*()-_=+[]{}:,.?']);

  if (!charsets.length) {
    message('pg-status', isAr ? 'اختر مجموعة أحرف واحدة على الأقل.' : 'Select at least one character set.', 'error');
    return null;
  }

  const excludeAmbiguous = byId('pg-ambiguous')?.checked;
  const excludeBrackets = byId('pg-no-brackets')?.checked;
  const guaranteeEach = byId('pg-guarantee-each')?.checked;
  const groupBlocks = byId('pg-group-blocks')?.checked;

  let cleanedSets = charsets.map(([name, chars]) => {
    let s = chars;
    if (excludeAmbiguous) s = s.replace(/[O0Il1|]/g, '');
    if (excludeBrackets) s = s.replace(/[{}[\]()/\\'"`~,;:.<>]/g, '');
    return [name, s];
  }).filter(([, chars]) => chars.length > 0);

  if (!cleanedSets.length) {
    message('pg-status', isAr ? 'لا توجد رموز متبقية بعد الفلترة. يرجى تعديل الخيارات.' : 'No characters left after filtering.', 'error');
    return null;
  }

  const allChars = cleanedSets.map(([, chars]) => chars).join('');
  const result = [];

  if (guaranteeEach) {
    cleanedSets.forEach(([, chars]) => {
      if (result.length < length) {
        result.push(randomChar(chars));
      }
    });
  }

  while (result.length < length) {
    result.push(randomChar(allChars));
  }

  shuffle(result);
  let finalPwd = result.join('');

  if (groupBlocks && finalPwd.length >= 8) {
    const blockSize = finalPwd.length > 20 ? 5 : 4;
    const parts = [];
    for (let i = 0; i < finalPwd.length; i += blockSize) {
      parts.push(finalPwd.slice(i, i + blockSize));
    }
    finalPwd = parts.join('-');
  }

  const poolSize = allChars.length;
  const entropy = Math.round((length * Math.log2(poolSize)) * 10) / 10;
  return { password: finalPwd, entropy, poolSize };
}

function generateDicewarePassphrase() {
  const wordCount = Number(wordsSlider?.value || 5);
  const lang = passphraseLang?.value || 'ar';
  const wordlist = lang === 'ar' ? ARABIC_DICEWARE : ENGLISH_DICEWARE;
  const sepChoice = separatorSelect?.value || '-';
  const casing = passphraseCase?.value || 'title';
  const extras = passphraseAddExtras?.value || 'number';

  const pickedWords = [];
  for (let i = 0; i < wordCount; i++) {
    let word = wordlist[secureIndex(wordlist.length)];
    if (casing === 'title') {
      word = word.charAt(0).toUpperCase() + word.slice(1);
    } else if (casing === 'upper') {
      word = word.toUpperCase();
    } else {
      word = word.toLowerCase();
    }
    pickedWords.push(word);
  }

  let separator = sepChoice;
  let finalPwd = '';
  if (sepChoice === 'num') {
    finalPwd = pickedWords.map((w, idx) => idx < pickedWords.length - 1 ? w + secureIndex(10) : w).join('');
  } else {
    finalPwd = pickedWords.join(separator);
  }

  let extraEntropyBits = 0;
  if (extras === 'number') {
    const num = String(secureIndex(100)).padStart(2, '0');
    finalPwd += (sepChoice === 'num' ? '' : '-') + num;
    extraEntropyBits = Math.log2(100);
  } else if (extras === 'symbol') {
    const num = String(secureIndex(100)).padStart(2, '0');
    const syms = '!@#$%^&*';
    const sym = syms[secureIndex(syms.length)];
    finalPwd += (sepChoice === 'num' ? '' : '-') + num + sym;
    extraEntropyBits = Math.log2(100 * syms.length);
  }

  const entropy = Math.round((wordCount * Math.log2(wordlist.length) + extraEntropyBits) * 10) / 10;
  return { password: finalPwd, entropy, poolSize: wordlist.length };
}

function generatePinCode() {
  const length = Number(pinLengthInput?.value || 6);
  const noRepeat = byId('pg-pin-no-repeat')?.checked;
  const noSeq = byId('pg-pin-no-seq')?.checked;

  let attempts = 0;
  while (attempts++ < 200) {
    const digits = [];
    for (let i = 0; i < length; i++) {
      digits.push(secureIndex(10));
    }

    if (noRepeat) {
      const allSame = digits.every(d => d === digits[0]);
      if (allSame) continue;
      let hasTriple = false;
      for (let i = 0; i < digits.length - 2; i++) {
        if (digits[i] === digits[i + 1] && digits[i + 1] === digits[i + 2]) {
          hasTriple = true;
          break;
        }
      }
      if (hasTriple) continue;
    }

    if (noSeq && length >= 3) {
      let isAsc = true;
      let isDesc = true;
      for (let i = 0; i < digits.length - 1; i++) {
        if ((digits[i] + 1) % 10 !== digits[i + 1]) isAsc = false;
        if ((digits[i] - 1 + 10) % 10 !== digits[i + 1]) isDesc = false;
      }
      if (isAsc || isDesc) continue;
    }

    const pinStr = digits.join('');
    const entropy = Math.round((length * Math.log2(10)) * 10) / 10;
    return { password: pinStr, entropy, poolSize: 10 };
  }

  // Fallback
  const digits = Array.from({ length }, () => secureIndex(10)).join('');
  return { password: digits, entropy: Math.round(length * Math.log2(10) * 10) / 10, poolSize: 10 };
}

// ============================================================================
// Password Analyzer (Entropy, Cracking Times, Syntax Highlighter)
// ============================================================================
function analyzePasswordMetrics(str) {
  let upper = 0;
  let lower = 0;
  let num = 0;
  let sym = 0;

  for (const ch of str) {
    if (/[0-9]/.test(ch)) num++;
    else if (/[A-Z]/.test(ch)) upper++;
    else if (/[a-z]/.test(ch)) lower++;
    else if (ch !== '-' && ch !== ' ' && ch !== '_') sym++;
  }

  let poolSize = 0;
  if (lower > 0) poolSize += 26;
  if (upper > 0) poolSize += 26;
  if (num > 0) poolSize += 10;
  if (sym > 0) poolSize += 33;
  if (poolSize === 0) poolSize = 1;

  const rawEntropy = Math.round((str.length * Math.log2(poolSize)) * 10) / 10;
  return { length: str.length, upper, lower, num, sym, poolSize, rawEntropy };
}

function renderSyntaxFormatted(str, masked) {
  if (!displayFormattedEl) return;
  if (!str) {
    displayFormattedEl.innerHTML = `<span style="color:var(--text-muted);">${isAr ? 'اضغط لتوليد كلمة مرور' : 'Click to generate password'}</span>`;
    return;
  }

  if (masked) {
    displayFormattedEl.innerHTML = `<span class="ch-masked">${'•'.repeat(Math.min(str.length, 36))}</span>`;
    return;
  }

  let html = '';
  for (const ch of str) {
    if (/[0-9]/.test(ch)) {
      html += `<span class="ch-num">${ch}</span>`;
    } else if (/[!@#$%^&*()_+\-=[\]{}|;:,.<>?/]/.test(ch)) {
      if (ch === '-' || ch === '_' || ch === '.') {
        html += `<span class="ch-sep">${ch}</span>`;
      } else {
        html += `<span class="ch-sym">${ch}</span>`;
      }
    } else if (/[A-Z]/.test(ch)) {
      html += `<span class="ch-upper">${ch}</span>`;
    } else if (ch === ' ') {
      html += `<span class="ch-sep"> </span>`;
    } else {
      html += `<span class="ch-lower">${ch}</span>`;
    }
  }
  displayFormattedEl.innerHTML = html;
}

function updateAuditUI(password) {
  if (!auditResultsContainer) return;
  if (!password) {
    auditResultsContainer.innerHTML = `<p style="color:var(--text-muted);font-size:0.85rem;margin:0;">${isAr ? 'اكتب كلمة المرور في الحقل أعلاه لبدء الفحص الفوري.' : 'Enter a password above to begin instant audit.'}</p>`;
    return;
  }

  const analysis = analyzePasswordMetrics(password);
  const checks = [
    { label: isAr ? 'طول كلمة المرور 12 خانة على الأقل' : 'At least 12 characters long', pass: analysis.length >= 12 },
    { label: isAr ? 'طول كلمة المرور 16 خانة (مستوى فائق)' : 'At least 16 characters long (Ultra)', pass: analysis.length >= 16 },
    { label: isAr ? 'تحتوي على أحرف كبيرة (A-Z)' : 'Contains uppercase letters (A-Z)', pass: analysis.upper > 0 },
    { label: isAr ? 'تحتوي على أحرف صغيرة (a-z)' : 'Contains lowercase letters (a-z)', pass: analysis.lower > 0 },
    { label: isAr ? 'تحتوي على أرقام (0-9)' : 'Contains numbers (0-9)', pass: analysis.num > 0 },
    { label: isAr ? 'تحتوي على رموز خاصة (!@#$)' : 'Contains special symbols (!@#$)', pass: analysis.sym > 0 },
    { label: isAr ? 'إنتروبيا تشفيرية قوية (≥ 70 بت)' : 'Strong cryptographic entropy (≥ 70 bits)', pass: analysis.rawEntropy >= 70 }
  ];

  let html = '';
  checks.forEach(c => {
    const cls = c.pass ? 'is-pass' : 'is-warn';
    const icon = c.pass ? '✓' : '⚠️';
    html += `<div class="pg-audit-check ${cls}"><span>${icon}</span><span>${c.label}</span></div>`;
  });

  auditResultsContainer.innerHTML = html;
}

// ============================================================================
// Master UI Update Engine
// ============================================================================
function updateUI(password, calculatedEntropy) {
  currentPassword = password;
  if (outputInputEl) outputInputEl.value = password;
  renderSyntaxFormatted(password, isMasked);

  const metrics = analyzePasswordMetrics(password);
  const entropy = calculatedEntropy || metrics.rawEntropy;

  // Strength score 0 to 4
  let strength = 0;
  let labelText = '';
  let color = '#ef4444';

  if (entropy >= 85) {
    strength = 4;
    labelText = isAr ? 'فائقة الأمان 🛡️' : 'Ultra Secure 🛡️';
    color = '#10b981';
  } else if (entropy >= 65) {
    strength = 3;
    labelText = isAr ? 'قوية جداً 🔒' : 'Very Strong 🔒';
    color = '#3b82f6';
  } else if (entropy >= 50) {
    strength = 2;
    labelText = isAr ? 'جيدة 👍' : 'Good 👍';
    color = '#f59e0b';
  } else if (entropy >= 35) {
    strength = 1;
    labelText = isAr ? 'متوسطة ⚠️' : 'Fair ⚠️';
    color = '#f97316';
  } else {
    strength = 0;
    labelText = isAr ? 'ضعيفة ❌' : 'Weak ❌';
    color = '#ef4444';
  }

  // Update Segments
  if (strengthSegments) {
    const segs = strengthSegments.querySelectorAll('.pg-bar-seg');
    segs.forEach((seg, idx) => {
      if (idx <= strength) {
        seg.style.backgroundColor = color;
        seg.style.opacity = '1';
      } else {
        seg.style.backgroundColor = '';
        seg.style.opacity = '';
      }
    });
  }

  if (strengthMeter) strengthMeter.value = strength;
  if (strengthLabel) {
    strengthLabel.textContent = labelText;
    strengthLabel.style.backgroundColor = `${color}22`;
    strengthLabel.style.color = color;
  }

  if (entropyLabel) {
    entropyLabel.textContent = isAr ? `${entropy} بت إنتروبيا` : `${entropy} bits entropy`;
  }

  // Cracking Time Matrix
  // S1 = 100/sec (Throttled)
  // S2 = 10,000/sec (Fast online)
  // S3 = 10^10/sec (GPU RTX 4090)
  // S4 = 10^14/sec (Supercomputer)
  const combs = Math.pow(2, entropy);
  const timeThrottled = formatTimeEstimate(combs / (2 * 100), isAr);
  const timeFast = formatTimeEstimate(combs / (2 * 10000), isAr);
  const timeGpu = formatTimeEstimate(combs / (2 * 1e10), isAr);
  const timeSuper = formatTimeEstimate(combs / (2 * 1e14), isAr);

  if (crackThrottledEl) crackThrottledEl.textContent = timeThrottled;
  if (crackFastEl) crackFastEl.textContent = timeFast;
  if (crackGpuEl) crackGpuEl.textContent = timeGpu;
  if (crackSuperEl) crackSuperEl.textContent = timeSuper;

  if (crackTimeLabel) {
    crackTimeLabel.innerHTML = `${isAr ? 'زمن الكسر (GPU):' : 'Crack Time (GPU):'} <strong>${timeGpu}</strong>`;
  }

  // Update Composition Chips
  if (chipLen) chipLen.innerHTML = `${isAr ? 'الطول:' : 'Length:'} <strong>${metrics.length}</strong>`;
  if (chipUpper) chipUpper.innerHTML = `${isAr ? 'كبيرة:' : 'Upper:'} <strong>${metrics.upper}</strong>`;
  if (chipLower) chipLower.innerHTML = `${isAr ? 'صغيرة:' : 'Lower:'} <strong>${metrics.lower}</strong>`;
  if (chipNum) chipNum.innerHTML = `${isAr ? 'أرقام:' : 'Numbers:'} <strong>${metrics.num}</strong>`;
  if (chipSym) chipSym.innerHTML = `${isAr ? 'رموز:' : 'Symbols:'} <strong>${metrics.sym}</strong>`;

  // Update QR if visible
  if (qrVisible) {
    renderQrCode();
  }
}

// ============================================================================
// QR Code Generator
// ============================================================================
async function renderQrCode() {
  if (!qrContainer || !currentPassword) return;
  try {
    const svgStr = await QRCode.toString(currentPassword, {
      type: 'svg',
      width: 140,
      margin: 1,
      color: { dark: '#0f172a', light: '#ffffff' }
    });
    qrContainer.innerHTML = svgStr;
  } catch {
    qrContainer.innerHTML = '';
  }
}

// ============================================================================
// Bulk Generator Engine
// ============================================================================
function generateBulkBatch() {
  if (!bulkListContainer) return;
  const count = Number(bulkCountSelect?.value || 10);
  const items = [];

  for (let i = 0; i < count; i++) {
    let pObj = generateCustomPassword();
    if (pObj) items.push(pObj.password);
  }

  let html = '';
  items.forEach((pwd, idx) => {
    html += `
      <div class="pg-bulk-item">
        <span class="pg-bulk-item-text">${pwd}</span>
        <button type="button" class="pg-bulk-copy-btn" data-copy-val="${pwd}">
          ${isAr ? 'نسخ' : 'Copy'}
        </button>
      </div>
    `;
  });

  bulkListContainer.innerHTML = html;

  // Bind single item copy buttons
  bulkListContainer.querySelectorAll('.pg-bulk-copy-btn').forEach(btn => {
    btn.addEventListener('click', async () => {
      const val = btn.dataset.copyVal;
      if (val) {
        await copyText(val);
        const originalText = btn.textContent;
        btn.textContent = isAr ? 'تم النسخ ✓' : 'Copied ✓';
        btn.style.color = '#10b981';
        setTimeout(() => {
          btn.textContent = originalText;
          btn.style.color = '';
        }, 1500);
      }
    });
  });

  message('pg-status', isAr ? `تم توليد ${items.length} كلمة مرور بنجاح.` : `Generated ${items.length} passwords successfully.`, 'success');
}

// ============================================================================
// Master Generator Trigger
// ============================================================================
function generate(fromUser = false) {
  if (fromUser) trackEvent('tool_started', 'password-generator');

  if (currentMode === 'audit') {
    const auditVal = auditInput?.value || '';
    updateUI(auditVal);
    updateAuditUI(auditVal);
    return;
  }

  let result = null;
  if (currentMode === 'passphrase') {
    result = generateDicewarePassphrase();
  } else if (currentMode === 'pin') {
    result = generatePinCode();
  } else {
    result = generateCustomPassword();
  }

  if (result) {
    updateUI(result.password, result.entropy);
    message('pg-status', isAr ? 'تم إنشاء كلمة المرور محلياً بأمان تام.' : 'Password generated securely on your device.', 'success');
    if (fromUser) trackEvent('tool_completed', 'password-generator');
  }
}

// ============================================================================
// Event Listeners & Interactive Binding
// ============================================================================

// Mode Switching Engine & Bidirectional Sync
function switchMode(mode, fromUser = true) {
  currentMode = mode;

  // Sync tabs
  document.querySelectorAll('.pg-tab-btn').forEach(b => {
    const isMatch = b.dataset.mode === mode;
    b.classList.toggle('is-active', isMatch);
    b.setAttribute('aria-selected', isMatch ? 'true' : 'false');
  });

  // Sync select
  const modeSelect = byId('pg-mode');
  if (modeSelect && modeSelect.value !== mode) {
    modeSelect.value = mode;
    if (modeSelect._kzCustomSelect) {
      modeSelect._kzCustomSelect.syncFromNative();
    }
  }

  // Show panel
  document.querySelectorAll('.pg-tab-pane').forEach(p => {
    p.hidden = true;
    p.classList.remove('is-active');
  });

  const activePane = byId(`pane-${mode}`);
  if (activePane) {
    activePane.hidden = false;
    activePane.classList.add('is-active');
  }

  if (mode === 'bulk') {
    generateBulkBatch();
  } else {
    generate(fromUser);
  }
}

// Mode Tabs Click
document.querySelectorAll('.pg-tab-btn').forEach(btn => {
  btn.addEventListener('click', () => {
    const mode = btn.dataset.mode;
    switchMode(mode, true);
  });
});

// Mode Select Change
const modeSelectEl = byId('pg-mode');
if (modeSelectEl) {
  modeSelectEl.addEventListener('change', () => {
    switchMode(modeSelectEl.value, true);
  });
}

// Length Slider & Number Input Sync
if (lengthSlider) {
  lengthSlider.addEventListener('input', (e) => {
    const val = e.target.value;
    if (lengthNumInput) lengthNumInput.value = val;
    if (lengthOut) lengthOut.value = val;
    updatePresetChipsActive(val);
    generate(false);
  });
}

if (lengthNumInput) {
  lengthNumInput.addEventListener('input', (e) => {
    let val = Number(e.target.value);
    if (val < 6) val = 6;
    if (val > 128) val = 128;
    if (lengthSlider) lengthSlider.value = val;
    if (lengthOut) lengthOut.value = val;
    updatePresetChipsActive(val);
    generate(false);
  });
}

function updatePresetChipsActive(val) {
  document.querySelectorAll('.pg-preset-chip').forEach(chip => {
    if (chip.dataset.length === String(val)) {
      chip.classList.add('is-active');
    } else {
      chip.classList.remove('is-active');
    }
  });
}

// Preset Chips
document.querySelectorAll('.pg-preset-chip').forEach(chip => {
  chip.addEventListener('click', () => {
    const len = chip.dataset.length;
    if (lengthSlider) lengthSlider.value = len;
    if (lengthNumInput) lengthNumInput.value = len;
    if (lengthOut) lengthOut.value = len;
    updatePresetChipsActive(len);
    generate(true);
  });
});

// Passphrase Words Slider
if (wordsSlider) {
  wordsSlider.addEventListener('input', (e) => {
    const val = e.target.value;
    if (wordsOut) wordsOut.value = isAr ? `${val} كلمات` : `${val} words`;
    generate(false);
  });
}

// Passphrase Selects
[passphraseLang, separatorSelect, passphraseCase, passphraseAddExtras].forEach(el => {
  el?.addEventListener('change', () => generate(true));
});

// Checkboxes for Custom Password
['pg-lower', 'pg-upper', 'pg-number', 'pg-symbol', 'pg-ambiguous', 'pg-no-brackets', 'pg-guarantee-each', 'pg-group-blocks'].forEach(id => {
  byId(id)?.addEventListener('change', () => generate(false));
});

// PIN Presets & Toggles
document.querySelectorAll('.pg-pin-btn').forEach(btn => {
  btn.addEventListener('click', () => {
    document.querySelectorAll('.pg-pin-btn').forEach(b => b.classList.remove('is-active'));
    btn.classList.add('is-active');
    if (pinLengthInput) pinLengthInput.value = btn.dataset.pin;
    generate(true);
  });
});

['pg-pin-no-repeat', 'pg-pin-no-seq'].forEach(id => {
  byId(id)?.addEventListener('change', () => generate(false));
});

// Regenerate Button with Smooth Rotation
if (generateBtn) {
  generateBtn.addEventListener('click', () => {
    generateBtn.classList.add('is-spinning');
    setTimeout(() => generateBtn.classList.remove('is-spinning'), 400);
    generate(true);
  });
}

// Visibility Toggle Button
if (toggleVisBtn) {
  toggleVisBtn.addEventListener('click', () => {
    isMasked = !isMasked;
    renderSyntaxFormatted(currentPassword, isMasked);
    const eyeSvg = isMasked
      ? `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9.88 9.88a3 3 0 1 0 4.24 4.24"/><path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68"/><path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61"/><line x1="2" y1="2" x2="22" y2="22"/></svg>`
      : `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg>`;
    toggleVisBtn.innerHTML = eyeSvg;
  });
}

// Show QR Code Button
if (showQrBtn && qrBox) {
  showQrBtn.addEventListener('click', () => {
    qrVisible = !qrVisible;
    qrBox.hidden = !qrVisible;
    if (qrVisible) renderQrCode();
  });
}

// Master Copy Button
if (copyBtn) {
  copyBtn.addEventListener('click', async () => {
    if (!currentPassword) return;
    try {
      await copyText(currentPassword);
      if (copyTextSpan) copyTextSpan.textContent = isAr ? 'تم النسخ ✓' : 'Copied ✓';
      copyBtn.style.background = '#10b981';
      message('pg-status', isAr ? 'تم نسخ كلمة المرور إلى الحافظة بنجاح.' : 'Password copied to clipboard.', 'success');
      trackEvent('tool_completed', 'password-generator');
      setTimeout(() => {
        if (copyTextSpan) copyTextSpan.textContent = isAr ? 'نسخ كلمة المرور' : 'Copy Password';
        copyBtn.style.background = '';
      }, 2000);
    } catch {
      message('pg-status', isAr ? 'تعذر النسخ إلى الحافظة.' : 'Copy failed.', 'error');
      trackEvent('tool_failed', 'password-generator');
    }
  });
}

// Click display box to select all password text
if (displayFormattedEl) {
  displayFormattedEl.addEventListener('click', () => {
    const sel = window.getSelection();
    if (sel) {
      sel.selectAllChildren(displayFormattedEl);
    }
  });
}

// Bulk Generator Actions
byId('pg-bulk-generate-btn')?.addEventListener('click', generateBulkBatch);

byId('pg-bulk-copy-all')?.addEventListener('click', async () => {
  const items = Array.from(bulkListContainer?.querySelectorAll('.pg-bulk-item-text') || []).map(el => el.textContent.trim());
  if (!items.length) return;
  try {
    await copyText(items.join('\n'));
    message('pg-status', isAr ? `تم نسخ ${items.length} كلمة مرور إلى الحافظة.` : `Copied ${items.length} passwords.`, 'success');
  } catch {
    message('pg-status', isAr ? 'تعذر نسخ الكل.' : 'Copy failed.', 'error');
  }
});

byId('pg-bulk-download')?.addEventListener('click', () => {
  const items = Array.from(bulkListContainer?.querySelectorAll('.pg-bulk-item-text') || []).map(el => el.textContent.trim());
  if (!items.length) return;
  const content = `# Kazakan Secure Password Batch\n# Generated: ${new Date().toISOString()}\n\n` + items.join('\n');
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
  saveBlob(blob, `kazakan-passwords-${Date.now()}.txt`);
  message('pg-status', isAr ? 'تم تحميل ملف كلمات المرور.' : 'File downloaded.', 'success');
});

// Auditor / Tester Input
if (auditInput) {
  auditInput.addEventListener('input', (e) => {
    const val = e.target.value;
    updateUI(val);
    updateAuditUI(val);
  });
}

if (auditToggleEye && auditInput) {
  auditToggleEye.addEventListener('click', () => {
    const isPass = auditInput.type === 'password';
    auditInput.type = isPass ? 'text' : 'password';
    auditToggleEye.innerHTML = isPass
      ? `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9.88 9.88a3 3 0 1 0 4.24 4.24"/><path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68"/><path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61"/><line x1="2" y1="2" x2="22" y2="22"/></svg>`
      : `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg>`;
  });
}

// ============================================================================
// Initialization
// ============================================================================
generate();
